import React, { useState } from 'react';
import { TouchableOpacity, Platform } from 'react-native';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  useToken,
  useMedia,
  Button,
  ButtonText,
  Icon,
} from '@gluestack-ui/themed';
import {
  Trophy, Lock, GraduationCap, Clock,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import Animated, {
  useAnimatedStyle,
  interpolate,
  Extrapolation,
  SharedValue,
} from 'react-native-reanimated';
import { getColorForModule } from '@/common/utils/getColorForModule';
import HtmlText from '@/common/components/HtmlText';
import { ModuleRoadmapPath } from './ModuleRoadmapPath';
import { Module } from '../types';

interface ModuleItemProps {
  /** The module data */
  item: Module;
  /** Index of the module in the list */
  index: number;
  /** Shared value of the vertical scroll position */
  scrollY: SharedValue<number>;
  /** Callback to handle opening a module */
  handleOpenModule: (params: { moduleId: string; courseId: string }) => void;
  /** Shared value of the page height */
  pageHeightSV: SharedValue<number>;
  /** Fixed page height value */
  pageHeight: number;
  /** Currently selected module (if any) */
  selectedModule?: { moduleId: string; courseId: string };
}

const OVERLAY_BG = '$bgOverlay';

/**
 * ModuleItem renders a full-page card for a module in the course screen.
 * It includes metadata, a roadmap, and action buttons.
 */
export const ModuleItem: React.FC<ModuleItemProps> = ({
  item,
  index,
  scrollY,
  handleOpenModule,
  pageHeightSV,
  pageHeight,
  selectedModule,
}) => {
  const { t } = useTranslation();
  const media = useMedia();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);
  const color = getColorForModule(item);
  // @ts-expect-error - color500 is a dynamic token
  const moduleColor = useToken('colors', `${color}500`) || '#000000';

  const courseTitle = item.courses?.[0]?.title;
  const { metadata } = item;
  const lecturer = item.author
    ? `${item.author.title || ''} ${item.author.firstname || ''} ${item.author.lastname || ''}`.trim()
      || item.author.name
      || ''
    : '';
  const cleanLecturer = lecturer.replace(/\s+/g, ' ').trim();
  const isGraded = item.uiState === 'graded';
  const isSubmitted = item.uiState === 'submitted';
  const isLocked = item.uiState === 'locked';

  // @ts-expect-error - textDisabled is a semantic token
  const textDisabledColor = useToken('colors', 'textDisabled');
  // @ts-expect-error - textHeading is a semantic token
  const textHeadingColor = useToken('colors', 'textHeading');

  const contentAnimatedStyle = useAnimatedStyle(() => {
    const height = Math.max(pageHeightSV.value, 100);
    const inputRange = [(index - 1) * height, index * height, (index + 1) * height];
    const opacity = interpolate(scrollY.value, inputRange, [0, 1, 0], Extrapolation.CLAMP);
    const translateY = interpolate(scrollY.value, inputRange, [40, 0, -40], Extrapolation.CLAMP);

    return {
      opacity,
      transform: [{ translateY }],
      width: '100%',
    };
  });

  const htmlContentStyle = {
    fontWeight: '500' as const,
    color: isLocked ? textDisabledColor : textHeadingColor,
    lineHeight: 24,
  };

  return (
    <Box
      w="100%"
      alignItems="center"
      h={pageHeight}
      testID={`module-item-${item.id}`}
    >
      <Animated.View
        style={contentAnimatedStyle}
      >
        <Box
          flex={1}
          w="100%"
          pb={media.md ? 180 : 150}
        >
          <VStack
            space="xl"
            px={media.md ? (Platform.OS === 'web' ? 60 : 20) : '$4'}
            pt={media.md ? 120 : 80}
            alignItems="flex-start"
            w="100%"
            maxWidth={1400}
            alignSelf="center"
          >
            {/* State Indicator Stamp */}
            {(isGraded || isSubmitted || isLocked) && (
              <Box
                position="absolute"
                right={media.md ? 10 : 20}
                top={media.md ? 130 : 90}
                zIndex={10}
                sx={{
                  transform: [{ rotate: '12deg' }],
                }}
              >
                <VStack
                  bg={isGraded ? '$statusSuccess' : isLocked ? '$bgHover' : '$statusInfo'}
                  px={media.md ? '$4' : '$3'}
                  py={media.md ? '$2' : '$1.5'}
                  borderRadius="$lg"
                  alignItems="center"
                  softShadow="3"
                  borderWidth={2}
                  borderColor={isLocked ? '$borderMedium' : '$white'}
                >
                  {isGraded ? (
                    <Icon as={Trophy} size={media.md ? 'lg' : 'sm'} color="white" />
                  ) : isLocked ? (
                    <Icon as={Lock} size={media.md ? 'lg' : 'sm'} color="$textHeading" />
                  ) : (
                    <Icon as={GraduationCap} size={media.md ? 'lg' : 'sm'} color="white" />
                  )}
                  <Text
                    color={isLocked ? '$textHeading' : isGraded ? '$textOnSuccess' : '$textOnInfo'}
                    fontWeight="900"
                    size="2xs"
                    textTransform="uppercase"
                    mt="$0.5"
                  >
                    {isGraded ? 'Graded' : isLocked ? 'Locked' : 'Submitted'}
                  </Text>
                </VStack>
              </Box>
            )}

            <VStack space="md" w="100%">
              <HStack space="md" alignItems="center">
                {!!isLocked && <Icon as={Lock} size="xs" color={moduleColor} />}
                {!!courseTitle && (
                  <Box bg="rgba(0,0,0,0.12)" px="$3" py="$1" borderRadius="$full">
                    <Text
                      size="xs"
                      fontWeight="900"
                      color="$textHeading"
                      textTransform="uppercase"
                      numberOfLines={1}
                      letterSpacing={2}
                    >
                      {courseTitle}
                    </Text>
                  </Box>
                )}
              </HStack>
              <Heading
                size={media.md ? '3xl' : 'xl'}
                color={isLocked ? '$textDisabled' : '$textHeading'}
                numberOfLines={2}
                fontSize={media.lg ? 64 : media.md ? 48 : 32}
                lineHeight={media.lg ? 80 : media.md ? 60 : 42}
                py="$1"
                fontWeight="900"
                letterSpacing={-2}
              >
                {item.title}
              </Heading>
              <HStack space="md" mt="$1" flexWrap="wrap" alignItems="center">
                {cleanLecturer !== '' && (
                  <Box bg="rgba(0,0,0,0.12)" px="$3" py="$1" borderRadius="$full">
                    <Text
                      size="xs"
                      fontWeight="900"
                      color="$textHeading"
                      textTransform="uppercase"
                      letterSpacing={1}
                    >
                      By
                      {' '}
                      {cleanLecturer}
                    </Text>
                  </Box>
                )}
                {metadata?.ects !== undefined && (
                  <HStack space="xs" alignItems="center" bg="rgba(0,0,0,0.12)" px="$3" py="$1" borderRadius="$full">
                    <Text
                      size="xs"
                      fontWeight="900"
                      color="$textHeading"
                      textTransform="uppercase"
                      letterSpacing={1}
                    >
                      {metadata.ects}
                      {' '}
                      ECTS
                    </Text>
                  </HStack>
                )}
                {metadata?.learningHours !== undefined && (
                  <HStack space="xs" alignItems="center" bg="rgba(0,0,0,0.12)" px="$3" py="$1" borderRadius="$full">
                    <Text
                      size="xs"
                      fontWeight="900"
                      color="$textHeading"
                      textTransform="uppercase"
                      letterSpacing={1}
                    >
                      {metadata.learningHours}
                      {' '}
                      HRS
                    </Text>
                  </HStack>
                )}
              </HStack>
            </VStack>

            <TouchableOpacity activeOpacity={0.8} onPress={() => setIsDescriptionExpanded(!isDescriptionExpanded)}>
              <Box maxWidth={800} w="100%">
                <HtmlText
                  content={metadata?.description || ''}
                  fontSize={media.md ? 16 : 14}
                  numberOfLines={isDescriptionExpanded ? undefined : media.md ? 4 : 3}
                  width={undefined}
                  applySubmissionFormatting={undefined}
                  contentStyle={htmlContentStyle}
                />
              </Box>
            </TouchableOpacity>

            <ModuleRoadmapPath
              levels={metadata?.levels}
              brandColor={moduleColor}
              isGraded={isGraded}
              isLocked={isLocked}
            />
          </VStack>
        </Box>

        {/* Fixed Footer with Button */}
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          px={media.md ? (Platform.OS === 'web' ? 80 : 20) : '$4'}
          pb={media.md ? '$12' : '$10'}
          pt="$4"
          bg="transparent"
        >
          <Box maxWidth={1400} w="100%" alignSelf="center">
            {!isSubmitted ? (
              <Button
                testID="module-open-button"
                size={media.md ? 'lg' : 'md'}
                variant={isLocked ? 'outline' : 'solid'}
                onPress={() => handleOpenModule({ moduleId: item.id, courseId: item.courses?.[0]?.id || '' })}
                borderRadius="$full"
                h={media.md ? 52 : 44}
                px={media.md ? '$10' : '$8'}
                bg={isLocked ? 'transparent' : isGraded ? '$statusSuccess' : moduleColor}
                borderColor={isLocked ? '$borderMedium' : isGraded ? '$statusSuccess' : moduleColor}
                $hover-bg={isGraded ? '$statusSuccess' : moduleColor}
                softShadow="3"
                w={300}
              >
                <ButtonText
                  fontWeight="900"
                  fontSize={media.md ? '$md' : '$sm'}
                  letterSpacing={1}
                  color={isLocked ? '$textSubtle' : isGraded ? '$textOnSuccess' : '$textOnBrand'}
                >
                  {isLocked
                    ? t('student.course.modulecard.button-unlockmodule').toUpperCase()
                    : isGraded
                      ? t('student.course.modulecard.button-viewgrade').toUpperCase()
                      : (selectedModule?.moduleId === item.id
                        ? t('student.course.modulecard.button-workonmodule')
                        : t('student.course.modulecard.button-openmodule')
                      ).toUpperCase()}
                </ButtonText>
              </Button>
            ) : (
              <HStack space="sm" alignItems="center" bg={OVERLAY_BG} px="$4" py="$3" borderRadius="$lg" softShadow="2">
                <Icon as={Clock} size="sm" color="$textSubtle" />
                <Text size="sm" color="$textSubtle" fontWeight="bold">
                  {t('student.course.modulecard.grading-due-date')}
                  :
                  {' '}
                  {item.state?.gradingDue ? new Date(item.state.gradingDue).toLocaleDateString() : 'TBD'}
                </Text>
              </HStack>
            )}
          </Box>
        </Box>
      </Animated.View>
    </Box>
  );
};
